/**
 * from: https://github.com/tailwindlabs/tailwindcss/blob/b67cbcf6ccaa58097cb6d8d7e0eb1fca1091ccca/packages/tailwindcss/src/utilities.ts#L33
 * But we comment out a bunch because these are the only ones we display. The rest are considered a custom value
 */
const DEFAULT_SPACING_SUGGESTIONS = ['0', '2', '4', '6', '8', '12', '24'] as const;

/**
 * Named size suggestions used across width, height, and min/max sizing utilities.
 * These represent semantic size values rather than numeric spacing values.
 */
export const DEFAULT_SIZE_SUGGESTIONS = ['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl'] as const;

/**
 * Generates an array of Tailwind spacing classes with the given prefix.
 * Includes 'px' variant and all DEFAULT_SPACING_SUGGESTIONS values.
 *
 * @param prefix - The Tailwind class prefix (e.g., 'px', 'py', 'mt', 'mb', 'gap')
 * @returns Array of spacing class names
 *
 * @example
 * buildSpacingClasses('px') // ['px-0', 'px-px', 'px-0.5', 'px-1', ...]
 * buildSpacingClasses('mt') // ['mt-0', 'mt-px', 'mt-0.5', 'mt-1', ...]
 */
export function buildSpacingClasses<T extends string>(prefix: T, options?: { skipNone?: boolean }) {
  const values = options?.skipNone ? DEFAULT_SPACING_SUGGESTIONS.slice(1) : DEFAULT_SPACING_SUGGESTIONS;
  return [...values.map((value) => `${prefix}-${value}`)] as const;
}

/**
 * Generates an array of Tailwind size classes with the given prefix.
 * Uses semantic size names (xs, sm, md, lg, xl, 2xl, 3xl).
 *
 * @param prefix - The Tailwind class prefix (e.g., 'w', 'h', 'max-w', 'min-h')
 * @returns Array of size class names
 *
 * @example
 * buildSizeClasses('w') // ['w-xs', 'w-sm', 'w-md', 'w-lg', 'w-xl', 'w-2xl', 'w-3xl']
 * buildSizeClasses('max-h') // ['max-h-xs', 'max-h-sm', 'max-h-md', ...]
 */
export function buildSizeClasses<T extends string>(prefix: T, excludes?: (typeof DEFAULT_SIZE_SUGGESTIONS)[number][]) {
  return [
    ...DEFAULT_SIZE_SUGGESTIONS.filter((s) => !excludes || !excludes.includes(s)).map((value) => `${prefix}-${value}`),
  ] as const;
}

// Pattern for a single CSS value (number + optional unit, or keyword)
const cssSizeValuePattern =
  '-?[\\d.]+(?:px|rem|em|%|vh|vw|ch|vmin|vmax|ex|cap|lh|rlh|cm|mm|in|pt|pc)?|auto|inherit|initial|unset|none|0';

// Full regex to capture 1-4 values for directional values (e.g. `margin: 10px 20px 30px 40px`)
const directionalShorthandRegex = new RegExp(
  `^(${cssSizeValuePattern})(?:\\s+(${cssSizeValuePattern}))?(?:\\s+(${cssSizeValuePattern}))?(?:\\s+(${cssSizeValuePattern}))?$`,
  'i',
);

/**
 * Parses CSS shorthand values (1-4 values) into per-side values using TRBL (top, right, bottom, left) rules.
 *
 * @example
 * parseDirectionalValue('10px')           // { top: '10px', right: '10px', bottom: '10px', left: '10px' }
 * parseDirectionalValue('0px 0px 3px')    // { top: '0px', right: '0px', bottom: '3px', left: '0px' }
 */
export function parseDirectionalValue(
  value: string,
): { top: string; right: string; bottom: string; left: string } | null {
  const match = value.trim().match(directionalShorthandRegex);
  if (!match) {
    return null;
  }
  const [, v1, v2, v3, v4] = match;
  if (!v1) {
    return null;
  }
  if (v1 && v2 && v3 && v4) {
    // 4 values: top, right, bottom, left
    return { top: v1, right: v2, bottom: v3, left: v4 };
  }
  if (v1 && v2 && v3) {
    // 3 values: top, right/left, bottom
    return { top: v1, right: v2, bottom: v3, left: v2 };
  }
  if (v1 && v2) {
    // 2 values: top/bottom, right/left
    return { top: v1, right: v2, bottom: v1, left: v2 };
  }
  // 1 value: all sides
  return { top: v1, right: v1, bottom: v1, left: v1 };
}
